import { IconBin } from '@cimpress-technology/react-streamline-icons';
import {
  Button,
  CodeBlock,
  colors,
  Label,
  Modal,
  ReactTablev6,
  Tooltip,
} from '@cimpress/react-components';
import React, { useEffect, useState } from 'react';
import { createAuthorizationCodeGrant } from '../../auth.tsx';
import { deleteSubscription, getSubscriptions } from '../../services/subscriptions.ts';
import { AlertTarget, CHANNELS, describeAlertTarget, formatChannelNames } from './alertTargets.ts';
import './subscription.css';

type Columns = {
  Header: string;
  accessor?: string | any;
  Cell?: ({ original }: { original: any }) => any;
  maxWidth?: number;
  id?: string;
}[];

type resourceFilter = {
  path: string;
  value: string;
  operator: string;
};

type SubscriptionType = {
  id: string;
  created: string;
  version: number;
  alertTargets?: AlertTarget[];
  resourceId?: string;
  subscriber: string;
  resourceType: string;
  actionNames?: string[];
  label?: string;
  resourceFilters: resourceFilter[];
};

// Label colours per channel. A channel without a case here (e.g. newly added in the API) gets the
// default colour until it is given its own.
const getChannelLabelStyle = (channel: string): React.CSSProperties => {
  switch (channel) {
    case CHANNELS.EMAIL:
      return { backgroundColor: colors.info.darker, color: colors.white };
    case CHANNELS.DIGEST:
      return { backgroundColor: colors.warning.darker, color: colors.white };
    case CHANNELS.HTTP:
      return { backgroundColor: colors.danger.darker, color: colors.white };
    case CHANNELS.INAPP:
      return { backgroundColor: colors.success.darker, color: colors.white };
    default:
      return { backgroundColor: colors.granite.darker, color: colors.white };
  }
};

const DeliveryTargets: React.FC<{ targets?: AlertTarget[] }> = ({ targets }) => {
  if (!targets?.length) {
    return <div>—</div>;
  }

  const notificationCenterUrl = import.meta.env.VITE_NOTIFICATION_CENTER_URL;

  return (
    <div>
      {targets.map(target => {
        const description = describeAlertTarget(target);
        const detail =
          target.channel === CHANNELS.INAPP && notificationCenterUrl ? (
            <a href={notificationCenterUrl} target="_blank" rel="noopener noreferrer">
              {description}
            </a>
          ) : (
            <span>{description}</span>
          );

        return (
          <div key={target.channel} className="delivery-target">
            <Label
              className="delivery-channel"
              text={target.channel}
              style={getChannelLabelStyle(target.channel)}
              size="sm"
            />
            {description && (
              <Tooltip
                contents={description}
                tooltipInnerStyle={{ minWidth: 'fit-content' }}
                containerClassName="delivery-target-detail"
              >
                {detail}
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Subscription: React.FC = () => {
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionType>();
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  const removeSubscription = async (subscriptionId: string): Promise<void> => {
    try {
      await deleteSubscription(subscriptionId);
      setSubscriptionData(subscriptionData.filter(({ id }) => id !== subscriptionId));
    } catch (error) {
      console.error(error);
    }
  };

  const columns: Columns = [
    {
      Header: 'Resource Type',
      accessor: 'resourceType',
      maxWidth: 200,
      Cell: ({ original }) => (
        <Tooltip contents={original.resourceType} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.resourceType}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Action Names',
      accessor: 'actionNames',
      maxWidth: 150,
      Cell: ({ original }) => {
        const actionNames = (original.actionNames ?? []).join(', ');

        return (
          <Tooltip contents={actionNames} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
            <div>{actionNames}</div>
          </Tooltip>
        );
      },
    },
    {
      Header: 'Resource Id',
      accessor: 'resourceId',
      maxWidth: 100,
      Cell: ({ original }) => (
        <Tooltip contents={original.resourceId} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.resourceId}</div>
        </Tooltip>
      ),
    },
    {
      id: 'delivery',
      Header: 'Delivery',
      maxWidth: 400,
      accessor: (data: SubscriptionType) => formatChannelNames(data.alertTargets),
      Cell: ({ original }) => <DeliveryTargets targets={original.alertTargets} />,
    },
    {
      Header: 'Filter',
      accessor: 'resourceFilters',
      Cell: ({ original }) => (
        <CodeBlock code={JSON.stringify(original.resourceFilters, null, 2)} />
      ),
    },
    {
      Header: 'Metadata',
      accessor: 'metadata',
      Cell: ({ original }) => (
        <CodeBlock code={JSON.stringify(original?.metadata || [], null, 2)} />
      ),
    },
    {
      Header: 'Subscriber',
      accessor: 'subscriber',
      maxWidth: 100,
      Cell: ({ original }) => (
        <Tooltip contents={original.subscriber} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.subscriber}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Created By',
      accessor: 'createdBy',
      maxWidth: 100,
      Cell: ({ original }) => (
        <Tooltip contents={original.createdBy} tooltipInnerStyle={{ minWidth: 'fit-content' }}>
          <div>{original.createdBy}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Created',
      accessor: 'created',
      maxWidth: 100,
      Cell: ({ original }: { original: SubscriptionType }) => (
        <Tooltip
          contents={`${new Date(original.created).toLocaleString()} (${original.created})`}
          tooltipInnerStyle={{ minWidth: 'fit-content' }}
        >
          <div>{new Date(original.created).toLocaleString()}</div>
        </Tooltip>
      ),
    },
    {
      Header: 'Action',
      maxWidth: 100,
      Cell: ({ original }) => (
        <>
          <Button
            variant="anchor"
            onClick={() => {
              setSelectedSubscription(original);
              setOpenModal(true);
            }}
          >
            <Tooltip contents={'Unsubscribe'}>
              <IconBin size={'lg'} weight={'fill'} />
            </Tooltip>
          </Button>
        </>
      ),
    },
  ];

  const refreshUserToken = (): void => {
    const authorizationCodeGrant = createAuthorizationCodeGrant('/dashboard');

    authorizationCodeGrant.login({ nextUri: `${import.meta.env.VITE_BASE_URL}/dashboard` });
  };

  useEffect(() => {
    const fetchSubscriptions = async (): Promise<void> => {
      try {
        const { subscriptions } = await getSubscriptions();

        setSubscriptionData(
          [...subscriptions].sort(
            (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
          ),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchSubscriptions();
  }, []);

  return (
    <>
      <Modal
        status={'danger'}
        show={openModal}
        onRequestHide={() => setOpenModal(false)}
        closeButton={true}
        title={'Delete Subscription'}
        footer={
          <div className="" style={{ justifyContent: 'space-between' }}>
            <Button onClick={() => setOpenModal(false)} style={{ marginRight: '32px' }}>
              Close
            </Button>
            <Button
              variant={'primary'}
              onClick={() => {
                removeSubscription(selectedSubscription.id);
                setOpenModal(false);
              }}
            >
              {'Delete'}
            </Button>
          </div>
        }
      >
        <p>Deleting this subscription will permanently remove it and cannot be retrieved.</p>
      </Modal>
      <div className="flex-col">
        <div className="flex-row">
          <h1 style={{ margin: '16px 0' }}>My Subscriptions</h1>
          <Button
            style={{
              maxWidth: '200px',
              maxHeight: '48px',
              marginTop: 'auto',
              marginLeft: 'auto',
              marginBottom: '16px',
            }}
            onClick={refreshUserToken}
          >
            Refresh Token
          </Button>
        </div>
        <ReactTablev6
          minRows={(subscriptionData || []).length ? 0 : 10}
          columns={columns}
          data={subscriptionData}
          loading={loadingData}
          keyField="Resource Id"
        />
      </div>
    </>
  );
};

export default Subscription;
